import type { ScheduleRecipient } from "@common-reducers/AddScheduledMessageButtonReducer";

/**
 * FE#230 — fan a single scheduled-message compose out to one persisted message
 * PER selected recipient (group/contact).
 *
 * Why this is its own unit: the multi-recipient loop used to `await … .unwrap()`
 * inline, so a rejection on recipient K aborted the whole loop and recipients
 * K+1..N were NEVER attempted — they silently dropped. The compose footer
 * dispatches this fire-and-forget (`dispatch(scheduleMessage(...)); handleClose()`)
 * with no await/catch, so the user got NO feedback either. #231 fixed the
 * first-recipient truncation (per-recipient idempotency key + Redux-single-source
 * recipients); this closes the remaining "one bad recipient drops the rest" hole.
 *
 * Contract:
 *   - EVERY recipient is attempted, independently — a failure on one can never
 *     skip the others.
 *   - Successful creates are collected in order and returned.
 *   - If at least one recipient persisted, we return the successes (best effort:
 *     persist as many groups as possible; a partial failure is logged, not thrown,
 *     so the successes are not rolled back).
 *   - Only when EVERY recipient failed do we rethrow (the first error) so the
 *     caller/UI can react to a total failure.
 *   - #1257: a partial failure is reported through `onPartialFailure` (how many of
 *     how many), so the user is told "2 of 5" instead of nothing.
 */
export interface FanOutPartialFailure {
  failed: number;
  total: number;
  firstError: unknown;
}

export async function fanOutScheduleRecipients<T>(
  contactList: readonly ScheduleRecipient[],
  createOne: (contact: ScheduleRecipient, index: number) => Promise<T | T[] | undefined>,
  onPartialFailure?: (failure: FanOutPartialFailure) => void,
): Promise<T[]> {
  const results: T[] = [];
  const failures: unknown[] = [];

  for (let i = 0; i < contactList.length; i++) {
    const contact = contactList[i];
    try {
      const result = await createOne(contact, i);
      if (result) {
        results.push(...(Array.isArray(result) ? result : [result]));
      }
    } catch (error) {
      failures.push(error);
      console.error(
        `[MULTIPLE RECIPIENTS] recipient ${i + 1}/${contactList.length} (${contact?.id ?? "?"}) failed to persist; continuing with the rest`,
        error,
      );
    }
  }

  // Nothing persisted at all → surface the failure to the caller.
  if (results.length === 0 && failures.length > 0) {
    throw failures[0];
  }

  if (failures.length > 0) {
    onPartialFailure?.({ failed: failures.length, total: contactList.length, firstError: failures[0] });
  }

  return results;
}
